// Phase 5.4 — Preview & Approve screen with editable layout (60/40 split).
//
// LEFT (60%): base image + regenerate-image button.
// RIGHT (40%): editable style dropdown + editable script textarea.
// FOOTER: Descartar / Crear mi video.
//
// State machine consumed from useVideoDraftStore:
//   - "directing"          → director Gemini running, show loader
//   - "ready"               → editable preview (the main screen)
//   - "editing_script"     → edit-script in flight
//   - "regenerating_image" → regenerate-image in flight
//   - "regenerating_style" → regenerate-style in flight (then directing)
//   - "approving"          → approve in flight, button disabled
//   - "rejecting"          → reject in flight, button disabled
//   - "rendering"          → post-approve, base image visible + progress
//   - "finished"           → final video player + Listo button
//   - "done"               → terminal (post-reject), auto-close
//   - "error"              → error screen with retry

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Copy,
  Image as ImageIcon,
  Loader2,
  Mic,
  Pencil,
  RefreshCw,
  Share2,
  Sparkles,
  XCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { dictionary, Language } from "@/utils/dictionary";
import { useVideoDraftStore } from "@/store/videoDraftStore";
import { useVideoInFlightStore } from "@/store/videoInFlightStore";
import {
  approveVideoDraft,
  generateVideoDraftAudio,
  getVideoDraft,
  getVideoDraftRenderPayloadPreview,
  setVideoDraftVoiceConfig,
} from "@/services/ads/videos/videoDraftService";
import { getErrorMessage } from "@/utils/handleApi";
import { VIDEO_DRAFT_STATUS } from "@/services/ads/videos/videoDraft.types";
import type {
  CuratedVoice,
  RegenerateDraftImageTarget,
  VideoDraftRenderPayloadPreview,
  VideoDraftResponse,
} from "@/services/ads/videos/videoDraft.types";
import { ButtonFluxi } from "@/components/common/Buttons/ButtonFluxi/ButtonFluxi";
import { VoiceLibrary, DEFAULT_VOICE_SETTINGS, type VoiceSettings } from "./VoiceLibrary";
import { VideoPlanningLoader } from "./VideoPlanningLoader";
import { cn } from "@/lib/utils";
import toast from "react-hot-toast";
import { isLocalHostname } from "@/utils/localHost";

interface PreviewApprovalProps {
  /** Called when the user wants to go back to the studio without rejecting the draft. */
  onBack: () => void;
  /** Called after a successful approve OR reject — closes the modal. */
  onDone: () => void;
  /** Called right after the user submits the draft to the generation queue. */
  onSubmitted?: () => void;
  /** Gender from the selected avatar, used only to pick a sensible default voice. */
  preferredVoiceGender?: "female" | "male" | string | null;
}

const MAX_WORDS_PER_SCRIPT_PART = 35;
const MAX_WORDS_TOTAL = MAX_WORDS_PER_SCRIPT_PART * 2;

// Phase 2 V9 REV 2 (Apr 19 2026) — duration-aware word budgets for modeling
// voice-over. Aligned with the director prompt budgets (see
// fluxi-mock-agent-config/agents/video_director_modeling_voiceover_v1.json)
// + empirically validated with Gemini TTS @ ~2.75 wps so the audio always
// fits the clip. Until this, the editor showed a generic 35-word cap that
// didn't change per duration — users editing a 15s script kept hitting
// "over limit" at 36 words even though 40 is fine for 15s, and users
// editing a 5s script had no visual hint they should stay under 13 words.
const MODELING_VO_WORD_BUDGETS: Record<number, number> = {
  5: 13,   // single-call narrative hook
  10: 28,
  15: 40,
  30: 85,
};

type PreviewImageAsset = {
  url?: string | null;
  label: string;
  subtitle: string;
  source: "generated" | "product";
  required?: boolean;
  target?: RegenerateDraftImageTarget | null;
};

function copyTextWithSelection(text: string) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  textarea.style.left = "0";
  textarea.style.top = "0";
  textarea.style.width = "1px";
  textarea.style.height = "1px";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  return copied;
}

async function copyTextToClipboard(text: string) {
  if (copyTextWithSelection(text)) return;
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  throw new Error("Copy command failed");
}

function toLocalPreviewAssetUrl(url?: string | null): string | null {
  if (!url) return null;

  // Local dev: CE uploads through the mock S3 server, but it may return an
  // anonymous trycloudflare URL that expires. The same file is still available
  // from the local mock server, so rewrite only for browser preview display.
  if (import.meta.env.DEV && typeof window !== "undefined") {
    try {
      const parsed = new URL(url);
      const isLocalApp = isLocalHostname(window.location.hostname);
      if (
        isLocalApp &&
        parsed.hostname.endsWith(".trycloudflare.com") &&
        parsed.pathname.startsWith("/uploads/")
      ) {
        return `http://localhost:8100${parsed.pathname}${parsed.search}`;
      }
    } catch {
      return url;
    }
  }

  return url;
}

function withPreviewRefreshToken(url?: string | null, token?: number): string {
  if (!url) return "";
  if (!token || url.startsWith("data:") || url.startsWith("blob:")) return url;
  return `${url}${url.includes("?") ? "&" : "?"}_fluxiPreviewRefresh=${token}`;
}

function wordCount(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

/**
 * Phase 5.4: split inteligente del guion unificado en script_part_a / part_b
 * antes de mandarlo al backend.
 *
 * El usuario ve un solo textarea con todo el guion. Internamente el video
 * son 2 escenas (Kling V3 Pro renderea de a 5-10s) y el backend valida que
 * cada parte tenga ≤25 palabras. Acá hacemos el split en el cliente para
 * mantener la experiencia simple.
 *
 * Estrategia:
 *   1. Si el texto entero tiene ≤25 palabras, no es combo: parte A = todo,
 *      parte B = null.
 *   2. Si es combo, busca el signo de puntuación final (".", "!", "?") más
 *      cercano al punto medio del texto en palabras. Eso da un corte
 *      narrativo natural.
 *   3. Si no hay puntuación cerca del medio, divide por mitad de palabras.
 */
function splitScriptIntoParts(
  fullText: string,
  isCombo: boolean
): { partA: string; partB: string | null } {
  const trimmed = fullText.trim();
  if (!isCombo) {
    return { partA: trimmed, partB: null };
  }

  const words = trimmed.split(/\s+/);
  if (words.length === 0) return { partA: "", partB: "" };

  // 1. Buscar puntuación cerca del medio.
  const midpoint = Math.floor(words.length / 2);
  // ventana ±30% del medio para buscar puntuación
  const windowSize = Math.max(2, Math.floor(words.length * 0.3));
  const start = Math.max(0, midpoint - windowSize);
  const end = Math.min(words.length - 1, midpoint + windowSize);

  let bestSplitIndex = -1;
  let bestDistance = Number.MAX_SAFE_INTEGER;
  for (let i = start; i <= end; i++) {
    if (/[.!?]$/.test(words[i])) {
      const distance = Math.abs(i + 1 - midpoint);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestSplitIndex = i + 1; // split AFTER this word
      }
    }
  }

  // 2. Fallback: split por mitad de palabras
  if (bestSplitIndex === -1) {
    bestSplitIndex = midpoint;
  }

  const partA = words.slice(0, bestSplitIndex).join(" ");
  const partB = words.slice(bestSplitIndex).join(" ");
  return { partA, partB };
}

function hasVoiceoverScript(dp: Record<string, unknown> | undefined): boolean {
  if (!dp) return false;
  if (typeof dp.full_voiceover === "string" && dp.full_voiceover.trim().length > 0) return true;
  if (typeof dp.script_beat_1 === "string" && dp.script_beat_1.trim().length > 0) return true;
  if (typeof dp.script === "string" && dp.script.trim().length > 0) return true;
  if (typeof dp.script_part_a === "string" && dp.script_part_a.trim().length > 0) return true;
  if (typeof dp.voiceover_script === "string" && dp.voiceover_script.trim().length > 0) return true;
  if (Array.isArray(dp.clips)) {
    const hasClipBeat = dp.clips.some((clip) => {
      if (!clip || typeof clip !== "object") return false;
      const beats = (clip as Record<string, unknown>).beats;
      if (!Array.isArray(beats)) return false;
      return beats.some((beat) => {
        if (!beat || typeof beat !== "object") return false;
        const voiceover = (beat as Record<string, unknown>).voiceover;
        return typeof voiceover === "string" && voiceover.trim().length > 0;
      });
    });
    if (hasClipBeat) return true;
  }
  if (!Array.isArray(dp.script_beats)) return false;
  return dp.script_beats.some((beat) => {
    if (!beat || typeof beat !== "object") return false;
    const voiceover = (beat as Record<string, unknown>).voiceover;
    return typeof voiceover === "string" && voiceover.trim().length > 0;
  });
}

function isPostApproveDraft(draft: VideoDraftResponse | null | undefined): draft is VideoDraftResponse {
  if (!draft) return false;
  if (draft.approved_at) return true;
  return (
    draft.status === VIDEO_DRAFT_STATUS.GENERATING_IMAGE ||
    draft.status === VIDEO_DRAFT_STATUS.WAITING_SCENES ||
    draft.status === VIDEO_DRAFT_STATUS.MERGING ||
    draft.status === VIDEO_DRAFT_STATUS.FINISHED
  );
}

function isDraftNotEditableConflict(error: unknown): boolean {
  const maybeAxiosError = error as {
    response?: {
      status?: number;
      data?: {
        trigger?: string;
      };
    };
  } | null;
  return (
    maybeAxiosError?.response?.status === 409 &&
    maybeAxiosError.response.data?.trigger === "DRAFT_NOT_EDITABLE"
  );
}

function labelFromImageKey(imageKey: string | null | undefined, fallback: string): string {
  const key = imageKey?.trim();
  if (!key) return fallback;
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (char) => char.toUpperCase());
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function recordList(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.map(recordValue).filter((item): item is Record<string, unknown> => !!item);
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function musicPromptFromDraft(draft: VideoDraftResponse | null | undefined): string {
  const voiceConfig = recordValue(draft?.voice_config);
  const persistedMusicPrompt = stringValue(voiceConfig?.music_prompt);
  if (persistedMusicPrompt) return persistedMusicPrompt;

  const directorPayload = recordValue(draft?.director_payload);
  const music = recordValue(directorPayload?.music);
  return stringValue(music?.prompt) ?? stringValue(music?.mood) ?? "";
}

function accentCityFromDraft(draft: VideoDraftResponse | null | undefined): string {
  const voiceConfig = recordValue(draft?.voice_config);
  return stringValue(voiceConfig?.accent_city) ?? "Medellín";
}

function booleanValue(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.trim().toLowerCase() === "true";
  return false;
}

export function PreviewApproval({ onBack, onDone, onSubmitted, preferredVoiceGender }: PreviewApprovalProps) {
  const { language } = useAuth();
  const lang = language as Language;

  const draft = useVideoDraftStore((s) => s.draft);
  const storeStatus = useVideoDraftStore((s) => s.storeStatus);
  const errorMessage = useVideoDraftStore((s) => s.errorMessage);
  const reset = useVideoDraftStore((s) => s.reset);
  const editScript = useVideoDraftStore((s) => s.editScript);
  const regenerateImage = useVideoDraftStore((s) => s.regenerateImage);
  const trackInFlightDraft = useVideoInFlightStore((s) => s.trackDraft);

  const t = (key: string, fallback: string) =>
    dictionary[key]?.[lang] || fallback;

  // ── Local edit buffer (single unified script editor) ──
  // Internamente sigue siendo combo (script_part_a + script_part_b para el
  // dispatch a Kling), pero al usuario le mostramos UN solo texto fluido.
  // El split en partes lo hacemos al guardar con splitScriptIntoParts().
  const payload = draft?.director_payload;
  const isCombo = !!payload?.script_part_b;
  // Phase 6 v2 — UGC multi-shot. The backend persists reference_images in
  // one of these layouts:
  //
  //   Phase 6 v2 combo (4):     [portrait, scene_a, scene_b, product]
  //   Phase 6 v2 non-combo (3): [portrait, scene_a, product]
  //   Phase 6.1 legacy (2):     [portrait, product]
  //
  // We render up to 4 thumbnails — portrait + scene_a + scene_b + product —
  // with hover-to-regenerate per slot. The legacy 2-image case still works
  // (renders just portrait + product).
  const isUgc = draft?.style_id === "ugc-testimonial";
  const isModelingSilent = draft?.style_id === "product-modeling";
  const isModelingVoiceover = draft?.style_id === "product-modeling-voiceover";
  const initialScriptA = payload?.script_part_a ?? "";
  const initialScriptB = payload?.script_part_b ?? "";
  const directVoiceoverScript = payload?.full_voiceover?.trim() ?? "";
  const initialFullScript = isModelingVoiceover
    ? directVoiceoverScript
    : payload?.voiceover_script?.trim()
    ? payload.voiceover_script.trim()
    : isCombo
      ? `${initialScriptA.trim()} ${initialScriptB.trim()}`.trim()
      : initialScriptA.trim();

  const [fullScriptDraft, setFullScriptDraft] = useState(initialFullScript);
  const [editingScript, setEditingScript] = useState(false);
  // Phase 2 V4 (Apr 18 2026) — Synchronous-ref guard for save script.
  // Previously a triple-click on "Guardar cambios" fired 3 duplicate
  // POST /edit-script. Idempotent on the backend (overwrites same fields
  // each time) but wasteful and confusing in logs. Same pattern used for
  // the approve button (Phase 3 fix).
  const saveScriptInFlightRef = useRef(false);
  const [isSavingScript, setIsSavingScript] = useState(false);

  // Phase 2 V2 — voice picker state for modeling-voiceover. The selection
  // survives audio regen (we keep what the user chose after a successful
  // TTS call) but defaults to whatever is already persisted on the draft
  // when they reopen the preview.
  const initialVoiceId =
    (draft?.voice_config?.voice_id as string | undefined) ?? null;
  const initialVoiceName =
    (draft?.voice_config?.voice_name as string | undefined) ?? null;
  const [selectedVoice, setSelectedVoice] = useState<CuratedVoice | null>(
    initialVoiceId && initialVoiceName
      ? ({
          voice_id: initialVoiceId,
          display_name: initialVoiceName,
        } as CuratedVoice)
      : null
  );
  // Phase 2 V2 — ElevenLabs-style voice controls. Initialized from the
  // draft's persisted voice_config (if the user is returning to a preview
  // they already tuned) or from the defaults. Changing the sliders doesn't
  // cost credits; the audition fires only when they click "Generar voz".
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings>(() => {
    const vc = draft?.voice_config as Record<string, unknown> | null | undefined;
    const persistedSpeed = vc?.speed;
    const persistedStability = vc?.stability;
    const persistedSimilarity = vc?.similarity_boost;
    const persistedStyle = vc?.style;
    return {
      speed: typeof persistedSpeed === "number" ? persistedSpeed : DEFAULT_VOICE_SETTINGS.speed,
      stability:
        typeof persistedStability === "number"
          ? persistedStability
          : DEFAULT_VOICE_SETTINGS.stability,
      similarity:
        typeof persistedSimilarity === "number"
          ? persistedSimilarity
          : DEFAULT_VOICE_SETTINGS.similarity,
      style:
        typeof persistedStyle === "number" ? persistedStyle : DEFAULT_VOICE_SETTINGS.style,
    };
  });
  const [accentCity, setAccentCity] = useState(() => accentCityFromDraft(draft));
  const [musicPrompt, setMusicPrompt] = useState(() => musicPromptFromDraft(draft));
  const [failedBaseImageUrl, setFailedBaseImageUrl] = useState<string | null>(null);
  const [failedPreviewImageUrls, setFailedPreviewImageUrls] = useState<string[]>([]);
  const [activePreviewImageIndex, setActivePreviewImageIndex] = useState(0);
  const [regeneratingPreviewImageIndex, setRegeneratingPreviewImageIndex] = useState<number | null>(null);
  const [recentlyUpdatedPreviewImageIndex, setRecentlyUpdatedPreviewImageIndex] = useState<number | null>(null);
  const [previewImageRefreshTokens, setPreviewImageRefreshTokens] = useState<Record<number, number>>({});
  const [loadedPreviewImageSrc, setLoadedPreviewImageSrc] = useState<string | null>(null);
  const [imageInstructionOpen, setImageInstructionOpen] = useState(false);
  const [imageInstruction, setImageInstruction] = useState("");
  const previousDraftReferenceIdRef = useRef<string | null>(null);
  const musicPromptTouchedRef = useRef(false);
  const recentlyUpdatedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Layer 3 synchronous in-flight guard for the Crear-mi-video flow.
  // The ref blocks duplicate clicks in the same event-loop tick; the
  // useState mirror drives the visible loading UI (button text,
  // disabled, spinner). We keep BOTH:
  //   · ref: atomic sync block — safe against race in one event-loop tick
  //   · state: triggers re-render so the user sees immediate feedback
  //
  // The approve flow may persist preview-side choices before `approve()`.
  // Local feedback flips on the first click so the button shows "Creando..."
  // while that short pre-approve request is in flight.
  const approveInFlightRef = useRef(false);
  const submittedToGalleryRef = useRef(false);
  const [isCreatingVideo, setIsCreatingVideo] = useState(false);
  const [createVideoError, setCreateVideoError] = useState<string | null>(null);
  const [copiedAdvancedPayload, setCopiedAdvancedPayload] = useState(false);
  const [renderPayloadPreview, setRenderPayloadPreview] = useState<VideoDraftRenderPayloadPreview | null>(null);
  const [renderPayloadLoading, setRenderPayloadLoading] = useState(false);
  const [renderPayloadError, setRenderPayloadError] = useState<string | null>(null);

  // Once the user has approved the preview, the gallery card owns the
  // long-running render feedback. This guards against race/resume cases where
  // the modal stays open and shows a second large "creating video" surface.
  useEffect(() => {
    if (
      (storeStatus === "rendering" || storeStatus === "finished") &&
      onSubmitted &&
      !submittedToGalleryRef.current
    ) {
      if (draft) {
        trackInFlightDraft({
          ...draft,
          status: VIDEO_DRAFT_STATUS.GENERATING_IMAGE,
        });
      }
      submittedToGalleryRef.current = true;
      onSubmitted();
    }
  }, [storeStatus, onSubmitted, draft, trackInFlightDraft]);

  // Reset local buffer whenever the draft (from server) changes,
  // e.g. after a successful edit / regenerate / pattern change.
  // Phase 6 V4b (Apr 20 2026) — removed setImageInstructionOpen +
  // setImageInstruction from the reset list; those states were deleted
  // together with the "Otra imagen / con instrucción" UI block. Leaving
  // the setters around crashed the bundle at mount → blank screen.
  useEffect(() => {
    setFullScriptDraft(initialFullScript);
    setEditingScript(false);
  }, [initialFullScript]);

  useEffect(() => {
    const currentReferenceId = draft?.reference_id ?? null;
    const nextMusicPrompt = musicPromptFromDraft(draft);
    if (previousDraftReferenceIdRef.current !== currentReferenceId) {
      previousDraftReferenceIdRef.current = currentReferenceId;
      musicPromptTouchedRef.current = false;
      setCreateVideoError(null);
      setActivePreviewImageIndex(0);
      setRegeneratingPreviewImageIndex(null);
      setRecentlyUpdatedPreviewImageIndex(null);
      setPreviewImageRefreshTokens({});
      setAccentCity(accentCityFromDraft(draft));
      setMusicPrompt(nextMusicPrompt);
    } else if (nextMusicPrompt && !musicPromptTouchedRef.current && musicPrompt !== nextMusicPrompt) {
      setMusicPrompt(nextMusicPrompt);
    }
    setFailedBaseImageUrl(null);
    setFailedPreviewImageUrls([]);
  }, [draft, musicPrompt]);

  useEffect(() => {
    return () => {
      if (recentlyUpdatedTimerRef.current) {
        clearTimeout(recentlyUpdatedTimerRef.current);
      }
    };
  }, []);

  const activePreviewResetKey = [
    draft?.reference_id ?? "",
    draft?.base_image_url ?? "",
    draft?.end_image_url ?? "",
    draft?.product_image_url ?? "",
    draft?.reference_images?.join("|") ?? "",
    String(activePreviewImageIndex),
    String(previewImageRefreshTokens[activePreviewImageIndex] ?? ""),
  ].join("::");

  useEffect(() => {
    setLoadedPreviewImageSrc(null);
    setImageInstructionOpen(false);
    setImageInstruction("");
  }, [activePreviewResetKey]);

  useEffect(() => {
    if (!draft?.reference_id || !payload) {
      setRenderPayloadPreview(null);
      return;
    }
    let cancelled = false;
    setRenderPayloadLoading(true);
    setRenderPayloadError(null);
    getVideoDraftRenderPayloadPreview(draft.reference_id)
      .then(({ res, error }) => {
        if (cancelled) return;
        if (res) {
          setRenderPayloadPreview(res);
        } else {
          setRenderPayloadPreview(null);
          setRenderPayloadError(error?.message || "No pudimos cargar el payload real.");
        }
      })
      .finally(() => {
        if (!cancelled) setRenderPayloadLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    draft?.reference_id,
    draft?.updated_at,
    draft?.base_image_url,
    draft?.end_image_url,
    draft?.reference_images,
    payload,
  ]);

  // Auto-close after a successful done state (post-reject).
  useEffect(() => {
    if (storeStatus === "done") {
      const tm = setTimeout(() => {
        reset();
        onDone();
      }, 1500);
      return () => clearTimeout(tm);
    }
  }, [storeStatus, reset, onDone]);

  // ─────────────── Loading / error / done / rendering / finished ───────────────

  if (storeStatus === "directing" || storeStatus === "regenerating_style" || (!draft && storeStatus !== "error")) {
    const isRegen = storeStatus === "regenerating_style";
    // Dynamic subtitle based on partial progress — when one of the two
    // parallel outputs has landed in the DB, we KNOW which half is pending
    // and can show a more specific subtitle on the same loader. NO
    // partial UI renders in the main canvas (that caused a jarring layout
    // flash on the final transition to the full preview — the script
    // would appear in a simple box and then jump into its real home in
    // the right-hand pane of PreviewApproval). Keeping a single layout
    // (loader → preview) is visually much smoother.
    const hasImage = !isRegen && !!draft?.base_image_url;
    const dp = !isRegen ? (draft?.director_payload as Record<string, unknown> | undefined) : undefined;
    const hasScript = hasVoiceoverScript(dp);

    const title = isRegen
      ? t("video-preview-regen-style-title", "Cambiando el estilo creativo")
      : t("video-preview-loading-title", "Planeando los detalles de tu video");

    // Subtitle hierarchy:
    //   - only image ready  → "Escribiendo el guión..."
    //   - only script ready → "Generando la imagen..."
    //   - neither yet       → default rotating messages from DirectorLoader
    let subtitle: string | undefined;
    if (!isRegen) {
      if (hasImage && !hasScript) {
        subtitle = t("video-preview-streaming-script", "Escribiendo el guión...");
      } else if (hasScript && !hasImage) {
        subtitle = t("video-preview-streaming-image", "Generando la imagen...");
      }
    }
    return <DirectorLoader title={title} subtitle={subtitle} />;
  }

  if (storeStatus === "error" || draft?.status === VIDEO_DRAFT_STATUS.ERROR) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-4">
        <XCircle className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-semibold text-foreground">
          {t("video-preview-error-title", "No pudimos crear el preview")}
        </h2>
        <p className="text-sm text-muted-foreground max-w-md">
          {errorMessage ||
            draft?.error_message ||
            t(
              "video-preview-error-subtitle",
              "Algo falló durante la generación del preview. No te cobramos nada."
            )}
        </p>
        <button
          type="button"
          onClick={() => {
            reset();
            onBack();
          }}
          className="mt-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
        >
          {t("video-preview-back-to-studio", "Volver al estudio")}
        </button>
      </div>
    );
  }

  if (storeStatus === "done") {
    const wasApproved = !!draft?.approved_at;
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-4">
        <CheckCircle2
          className={
            wasApproved ? "h-12 w-12 text-green-500" : "h-12 w-12 text-muted-foreground"
          }
        />
        <h2 className="text-xl font-semibold text-foreground">
          {wasApproved
            ? t("video-preview-approved-title", "¡Aprobado!")
            : t("video-preview-rejected-title", "Cancelado")}
        </h2>
        <p className="text-sm text-muted-foreground max-w-md">
          {wasApproved
            ? t("video-preview-approved-subtitle", "Tu video se está creando.")
            : t("video-preview-rejected-subtitle", "El preview fue descartado. No se creó ningún video.")}
        </p>
      </div>
    );
  }

  // ── Data derivation for ready / rendering / finished ──
  if (!draft || !payload) return null;

  const productName = draft.product_name || "tu producto";
  const fullScript = fullScriptDraft;
  // Modeling has two variants: silent (no voice, no script shown) and
  // voice-over (shows the full director voiceover for review before approval).
  const isApproving = storeStatus === "approving";
  const isRejecting = storeStatus === "rejecting";
  const isEditingScriptInFlight = storeStatus === "editing_script";
  const isRegenImage = storeStatus === "regenerating_image";
  const isBusy = isApproving || isRejecting || isEditingScriptInFlight || isRegenImage;
  const klingPreviewJson = renderPayloadPreview
    ? JSON.stringify(renderPayloadPreview, null, 2)
    : "";

  const regenCount = draft.regenerate_count ?? 0;
  const maxRegen = import.meta.env.DEV
    ? Math.max(draft.max_regenerate_count ?? 3, 25)
    : draft.max_regenerate_count ?? 3;
  const regenLeft = Math.max(0, maxRegen - regenCount);
  const canRegen = regenLeft > 0 && !isBusy;

  // ── Rendering state (post-approve) ──
  if (storeStatus === "rendering") {
    const baseImage = draft.base_image_url;
    return (
      <div className="flex flex-col h-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-foreground leading-tight">
              {t("video-render-title", "Estamos creando tu video")}
            </h2>
            <p className="text-sm text-muted-foreground leading-tight truncate max-w-[320px]">
              {productName}
            </p>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col items-center gap-6">
          {baseImage ? (
            <div className="relative w-full max-w-xs aspect-[9/16] rounded-2xl overflow-hidden border border-border bg-muted/20 shadow-lg">
              <img src={baseImage} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                <Loader2 className="h-4 w-4 text-white animate-spin" />
                <span className="text-xs text-white/90 font-medium">
                  {t("video-render-progress", "Casi listo")}
                </span>
              </div>
            </div>
          ) : (
            <div className="w-full max-w-xs aspect-[9/16] rounded-2xl border border-border bg-muted/20 flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-10 w-10 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">
                {t("video-render-warming", "Preparando tu escena")}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (storeStatus === "finished" && draft.video_url) {
    const shareVideo = async () => {
      const publicUrl = draft.video_url;
      if (!publicUrl) return;

      try {
        if (navigator.share) {
          await navigator.share({
            title: productName || t("video-render-done-title", "Tu video está listo"),
            text: t("video-share-text", "Mira este video creado en Fluxi"),
            url: publicUrl,
          });
          return;
        }

        await navigator.clipboard.writeText(publicUrl);
        toast.success(t("video-share-copied", "Link copiado"));
      } catch (error) {
        if ((error as Error)?.name === "AbortError") return;
        console.warn("[PreviewApproval] share failed:", error);
        try {
          await navigator.clipboard.writeText(publicUrl);
          toast.success(t("video-share-copied", "Link copiado"));
        } catch {
          toast.error(t("video-share-error", "No pudimos copiar el link"));
        }
      }
    };

    return (
      <div className="flex flex-col h-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-foreground leading-tight">
              {t("video-render-done-title", "Tu video está listo")}
            </h2>
            <p className="text-sm text-muted-foreground leading-tight truncate max-w-[320px]">
              {productName}
            </p>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-6 flex flex-col items-center justify-center gap-6">
          <div className="w-full max-w-xs aspect-[9/16] rounded-2xl overflow-hidden border border-border bg-black shadow-lg">
            <video
              src={draft.video_url}
              controls
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border flex-shrink-0">
          <button
            type="button"
            onClick={shareVideo}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold border border-border bg-white text-foreground hover:bg-muted/40 transition-colors"
          >
            <Share2 className="h-4 w-4" />
            {t("video-share-action", "Compartir")}
          </button>
          <button
            type="button"
            onClick={() => {
              reset();
              onDone();
            }}
            className="px-6 py-2.5 rounded-lg text-sm font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity shadow-sm"
          >
            {t("video-render-done-action", "Listo")}
          </button>
        </div>
      </div>
    );
  }

  // ─────────────── READY state — editable preview (60/40 split) ───────────────

  // Script editor: 1 textarea unificado, contador de palabras totales.
  // Al guardar, splitScriptIntoParts hace el split inteligente cliente-side
  // y mandamos las 2 partes al backend (que ya espera ese contrato).
  const totalWords = wordCount(fullScriptDraft);
  // Phase 2 V9 REV 2 — modeling voice-over scripts have duration-specific
  // word budgets (13 / 28 / 40 for 5s / 10s / 15s). Other styles keep the
  // generic 35/70 limit.
  const draftDuration = typeof draft?.duration === "number" ? draft.duration : null;
  const wordLimit = (() => {
    if (isModelingVoiceover && draftDuration && MODELING_VO_WORD_BUDGETS[draftDuration]) {
      return MODELING_VO_WORD_BUDGETS[draftDuration];
    }
    return isCombo ? MAX_WORDS_TOTAL : MAX_WORDS_PER_SCRIPT_PART;
  })();
  const scriptDirty = fullScriptDraft.trim() !== initialFullScript.trim();
  const scriptOverLimit = totalWords > wordLimit;
  // Amber warning when the user is within 2 words of the hard limit, so
  // they get a heads-up before hitting the red state.
  const scriptNearLimit = !scriptOverLimit && totalWords >= wordLimit - 2;
  const canSaveScript =
    scriptDirty && !scriptOverLimit && fullScriptDraft.trim().length > 0 && !isBusy && !isSavingScript;
  const previewImageUrl =
    draft.base_image_url && failedBaseImageUrl !== draft.base_image_url
      ? draft.base_image_url
      : draft.product_image_url;
  const referenceImages = draft.reference_images?.filter(Boolean) ?? [];
  const videoBaseImages = payload?.video_base_images?.filter((item) => item?.image_key) ?? [];
  const renderPayloadRecord = recordValue(renderPayloadPreview);
  const renderVideoBaseImages = recordList(renderPayloadRecord?.video_base_images);
  const renderElementMap = recordList(renderPayloadRecord?.element_map);
  const payloadRecord = recordValue(payload);
  const renderReferences = recordValue(payloadRecord?.render_references);
  const fallbackAvatarElementUrl =
    stringValue(renderReferences?.seedance_identity_image_url) ??
    stringValue(renderReferences?.selected_avatar_image_url);
  const fallbackProductElementUrl =
    stringValue(renderReferences?.product_image_url) ??
    draft.product_image_url;
  const fallbackElementMap: Record<string, unknown>[] = [];
  if (fallbackAvatarElementUrl) {
    fallbackElementMap.push({ element_key: "@Image1", role: "avatar", url: fallbackAvatarElementUrl });
  }
  if (fallbackProductElementUrl) {
    fallbackElementMap.push({ element_key: "@Image2", role: "product", url: fallbackProductElementUrl });
  }
  const hasAvatarElement = renderElementMap.some((item) => {
    const role = stringValue(item.role);
    const elementKey = stringValue(item.element_key);
    return role === "avatar" || elementKey === "@Image1";
  });
  const hasProductElement = renderElementMap.some((item) => {
    const role = stringValue(item.role);
    const elementKey = stringValue(item.element_key);
    return role === "product" || elementKey === "@Image2";
  });
  const availableElementMap =
    renderElementMap.length > 0
      ? [
          ...(!hasAvatarElement && fallbackAvatarElementUrl
            ? [{ element_key: "@Image1", role: "avatar", url: fallbackAvatarElementUrl }]
            : []),
          ...renderElementMap,
          ...(!hasProductElement && fallbackProductElementUrl
            ? [{ element_key: "@Image2", role: "product", url: fallbackProductElementUrl }]
            : []),
        ]
      : fallbackElementMap;
  const expectedModelingRefs = videoBaseImages.length > 0 ? videoBaseImages.length : 4;
  const modelingVoiceoverAssetsReady = !isModelingVoiceover || referenceImages.length >= expectedModelingRefs;
  const legacyModelingReferenceLabels = [
    {
      label: t("video-preview-asset-modeling-problem", "Avatar con dolor"),
      subtitle: t("video-preview-asset-modeling-problem-sub", "Estado inicial del problema"),
      target: "problem_avatar" as RegenerateDraftImageTarget,
    },
    {
      label: t("video-preview-asset-modeling-product", "Avatar con producto"),
      subtitle: t("video-preview-asset-modeling-product-sub", "Presentación principal"),
      target: "integrated_action" as RegenerateDraftImageTarget,
    },
    {
      label: t("video-preview-asset-modeling-clean", "Producto limpio"),
      subtitle: t("video-preview-asset-modeling-clean-sub", "Referencia del producto"),
      target: "clean_product" as RegenerateDraftImageTarget,
    },
    {
      label: t("video-preview-asset-modeling-usage", "Uso del producto"),
      subtitle: t("video-preview-asset-modeling-usage-sub", "Demostración o POV"),
      target: "usage_pov" as RegenerateDraftImageTarget,
    },
  ];
  const modelingReferenceLabels = videoBaseImages.length > 0
    ? videoBaseImages.map((item, index) => ({
        label: labelFromImageKey(item.image_key, t("video-preview-asset-extra", `Imagen ${index + 1}`)),
        subtitle: item.start_image_usage
          ? t("video-preview-asset-start-image-sub", "Start image del clip")
          : t("video-preview-asset-extra-sub", "Referencia visual"),
        target: item.image_key as RegenerateDraftImageTarget,
      }))
    : legacyModelingReferenceLabels;
  const directorStartKeys = new Set(
    (payload?.clips ?? [])
      .map((clip) => clip.start_image_key?.trim())
      .filter((key): key is string => !!key)
  );
  const clipPartByStartKey = new Map(
    (payload?.clips ?? [])
      .map((clip, index) => {
        const key = clip.start_image_key?.trim();
        if (!key) return null;
        return [
          key,
          clip.clip_part?.trim() || String.fromCharCode(65 + index),
        ] as const;
      })
      .filter((item): item is readonly [string, string] => !!item)
  );
	  const directorImageAssets = videoBaseImages.map((item, index) => ({
	    image_key: item.image_key,
	    prompt: item.prompt,
	    input_images: item.input_images ?? item.reference_roles,
	    start_image_usage: item.start_image_usage,
	    element_usage: item.element_usage,
	    element_key: item.element_key,
    url: referenceImages[index] ?? null,
  }));
  const resolvedVideoBaseAssets =
    renderVideoBaseImages.length > 0 ? renderVideoBaseImages : directorImageAssets;
  const startFrameAssets: PreviewImageAsset[] = resolvedVideoBaseAssets
    .filter((item) => {
      const key = stringValue(item.image_key);
      return booleanValue(item.start_image_usage) || (!!key && directorStartKeys.has(key));
    })
    .map((item, index) => {
      const key = stringValue(item.image_key);
      const part = key ? clipPartByStartKey.get(key) : null;
      return {
        url: toLocalPreviewAssetUrl(stringValue(item.url)),
        label: part
          ? t("video-preview-start-frame-part", `Start frame ${part}`).replace("{part}", part)
          : t("video-preview-start-frame", `Start frame ${index + 1}`).replace("{index}", String(index + 1)),
        subtitle: labelFromImageKey(key, t("video-preview-start-frame-sub", "Imagen base del clip")),
        source: "generated",
        required: true,
        target: key as RegenerateDraftImageTarget | null,
      };
    });
  const directorElementAssets: PreviewImageAsset[] = resolvedVideoBaseAssets
    .filter((item) => booleanValue(item.element_usage))
    .map((item, index) => {
      const key = stringValue(item.image_key);
      const elementKey = stringValue(item.element_key);
      return {
        url: toLocalPreviewAssetUrl(stringValue(item.url)),
        label: elementKey
          ? `${elementKey} · ${labelFromImageKey(key, t("video-preview-element", `Element ${index + 1}`))}`
          : t("video-preview-element", `Element ${index + 1}`).replace("{index}", String(index + 1)),
        subtitle: t("video-preview-element-sub", "Referencia visual"),
        source: "generated",
        target: key as RegenerateDraftImageTarget | null,
      };
    });
	  const baseElementAssets: PreviewImageAsset[] = availableElementMap.map((item, index) => {
	    const role = stringValue(item.role);
	    const elementKey = stringValue(item.element_key) ?? `@Image${index + 1}`;
    const isProduct = role === "product";
    const readableRole =
      role === "avatar"
        ? t("video-preview-element-avatar", "Avatar")
        : role === "product"
          ? t("video-preview-element-product", "Producto")
          : labelFromImageKey(role, t("video-preview-element", `Element ${index + 1}`));
    return {
      url: toLocalPreviewAssetUrl(stringValue(item.url)),
      label: `${elementKey} · ${readableRole}`,
      subtitle: t("video-preview-element-sub", "Referencia visual"),
      source: isProduct ? "product" : "generated",
    };
  });
  const modelingPreviewAssets = [
    ...startFrameAssets,
    ...directorElementAssets,
    ...baseElementAssets,
  ];
  const ugcReferenceLabels =
    referenceImages.length >= 4
      ? [
          {
            label: t("video-preview-asset-ugc-avatar", "Avatar"),
            subtitle: t("video-preview-asset-ugc-avatar-sub", "Identidad del protagonista"),
          },
          {
            label: t("video-preview-asset-ugc-scene-a", "Escena 1"),
            subtitle: t("video-preview-asset-ugc-scene-a-sub", "Primer plano del video"),
          },
          {
            label: t("video-preview-asset-ugc-scene-b", "Escena 2"),
            subtitle: t("video-preview-asset-ugc-scene-b-sub", "Segundo plano del video"),
          },
          {
            label: t("video-preview-asset-product", "Producto"),
            subtitle: t("video-preview-asset-product-sub", "Referencia del catálogo"),
          },
        ]
      : [
          {
            label: t("video-preview-asset-ugc-avatar", "Avatar"),
            subtitle: t("video-preview-asset-ugc-avatar-sub", "Identidad del protagonista"),
          },
          {
            label: t("video-preview-asset-ugc-scene-a", "Escena 1"),
            subtitle: t("video-preview-asset-ugc-scene-a-sub", "Primer plano del video"),
          },
          {
            label: t("video-preview-asset-product", "Producto"),
            subtitle: t("video-preview-asset-product-sub", "Referencia del catálogo"),
          },
        ];
  const referenceLabels = isModelingVoiceover ? modelingReferenceLabels : ugcReferenceLabels;
  const referenceAssets: PreviewImageAsset[] = isModelingVoiceover
    ? (modelingPreviewAssets.length > 0 ? modelingPreviewAssets : modelingReferenceLabels.map((item, index) => ({
        url:
          toLocalPreviewAssetUrl(
            referenceImages[index] ??
              (index === 0 ? draft.base_image_url : null) ??
              (videoBaseImages.length === 0 && index === 2 ? draft.product_image_url : null)
          ),
        label: item.label,
        subtitle: item.subtitle,
        source: videoBaseImages.length === 0 && index === 2 ? "product" : "generated",
        required: true,
        target: item.target,
      })))
    : referenceImages.map((url, index) => ({
        url: toLocalPreviewAssetUrl(url),
        label:
          referenceLabels[index]?.label ??
          t("video-preview-asset-extra", `Imagen ${index + 1}`),
        subtitle:
          referenceLabels[index]?.subtitle ??
          t("video-preview-asset-extra-sub", "Referencia visual"),
        source: index === referenceImages.length - 1 ? "product" : "generated",
      }));
  const fallbackAssets: PreviewImageAsset[] = [
    ...(previewImageUrl
      ? [
          {
            url: toLocalPreviewAssetUrl(previewImageUrl),
            label:
              previewImageUrl === draft.product_image_url
                ? t("video-preview-asset-product", "Producto")
                : t("video-preview-asset-main", "Imagen principal"),
            subtitle:
              previewImageUrl === draft.product_image_url
                ? t("video-preview-asset-product-sub", "Referencia del catálogo")
                : t("video-preview-asset-main-sub", "Imagen generada para el video"),
            source: previewImageUrl === draft.product_image_url ? "product" : "generated",
          } satisfies PreviewImageAsset,
        ]
      : []),
    ...(draft.end_image_url
      ? [
          {
            url: toLocalPreviewAssetUrl(draft.end_image_url),
            label: t("video-preview-asset-end", "Imagen final"),
            subtitle: t("video-preview-asset-end-sub", "Resolución o segundo estado"),
            source: "generated",
          } satisfies PreviewImageAsset,
        ]
      : []),
    ...(draft.product_image_url && draft.product_image_url !== previewImageUrl
      ? [
          {
            url: toLocalPreviewAssetUrl(draft.product_image_url),
            label: t("video-preview-asset-product", "Producto"),
            subtitle: t("video-preview-asset-product-sub", "Referencia del catálogo"),
            source: "product",
          } satisfies PreviewImageAsset,
        ]
      : []),
  ];
  const previewImageAssets = referenceAssets.length > 0 ? referenceAssets : fallbackAssets;
  const availablePreviewAssets = previewImageAssets.filter((asset) => !!asset.url);
  const activePreviewAssets = isModelingVoiceover
    ? previewImageAssets
    : availablePreviewAssets.length > 0
      ? availablePreviewAssets
      : previewImageAssets;
  const safeActivePreviewIndex = Math.min(
    activePreviewImageIndex,
    Math.max(activePreviewAssets.length - 1, 0),
  );
  const activePreviewAsset = activePreviewAssets[safeActivePreviewIndex] ?? null;
  const activePreviewImageSrc = withPreviewRefreshToken(
    activePreviewAsset?.url,
    previewImageRefreshTokens[safeActivePreviewIndex],
  );
  const activePreviewImageFailed =
    !!activePreviewAsset?.url && failedPreviewImageUrls.includes(activePreviewAsset.url);
  const activePreviewImageLoaded = !!activePreviewImageSrc && loadedPreviewImageSrc === activePreviewImageSrc;
  const isActivePreviewImageRegenerating =
    isRegenImage &&
    (regeneratingPreviewImageIndex === null || regeneratingPreviewImageIndex === safeActivePreviewIndex);
  const isActivePreviewImageRecentlyUpdated =
    recentlyUpdatedPreviewImageIndex === safeActivePreviewIndex;

  const goPreviewImage = (direction: -1 | 1) => {
    if (activePreviewAssets.length <= 1) return;
    setActivePreviewImageIndex((prev) => {
      const next = prev + direction;
      if (next < 0) return activePreviewAssets.length - 1;
      if (next >= activePreviewAssets.length) return 0;
      return next;
    });
  };

  const handleSaveScript = async () => {
    // Phase 2 V4 — synchronous ref guard blocks triple-click races BEFORE
    // React can flush the disabled state to the button. Without this, a
    // fast triple-click on "Guardar cambios" fires 3 POST /edit-script
    // calls (verified in network log Apr 18 2026).
    if (saveScriptInFlightRef.current) return;
    saveScriptInFlightRef.current = true;
    setIsSavingScript(true);
    try {
      const { partA, partB } = splitScriptIntoParts(fullScriptDraft, isCombo);
      // Validación final: si después del split una parte excede el límite,
      // el backend devuelve un error claro y el usuario verá el toast.
      const ok = await editScript(partA, partB);
      if (ok) {
        // Auto-close the editor on success — gives the user the signal
        // that the save landed (the editor folding closed + toast.success
        // from the store). Leaves it open on failure so they can retry
        // without losing their edits.
        setEditingScript(false);
      }
    } finally {
      saveScriptInFlightRef.current = false;
      setIsSavingScript(false);
    }
  };

  const handleRegenerateActiveImage = async () => {
    const target = activePreviewAsset?.target;
    if (!target || !canRegen) return;
    const instruction = imageInstruction.trim();
    if (!instruction) {
      setImageInstructionOpen(true);
      return;
    }
    const requestedIndex = safeActivePreviewIndex;
    setRegeneratingPreviewImageIndex(requestedIndex);
    setRecentlyUpdatedPreviewImageIndex(null);
    const ok = await regenerateImage(instruction, target);
    setRegeneratingPreviewImageIndex(null);
    if (!ok) return;

    setImageInstruction("");
    setImageInstructionOpen(false);
    setActivePreviewImageIndex(requestedIndex);
    setPreviewImageRefreshTokens((prev) => ({ ...prev, [requestedIndex]: Date.now() }));
    setRecentlyUpdatedPreviewImageIndex(requestedIndex);
    if (recentlyUpdatedTimerRef.current) {
      clearTimeout(recentlyUpdatedTimerRef.current);
    }
    recentlyUpdatedTimerRef.current = setTimeout(() => {
      setRecentlyUpdatedPreviewImageIndex(null);
      recentlyUpdatedTimerRef.current = null;
    }, 1800);
  };

  const handleCancelEdit = () => {
    setFullScriptDraft(initialFullScript);
    setEditingScript(false);
  };

  return (
    <div className="flex flex-col h-full max-h-[90vh] overflow-hidden">
      {/* Header — same product shell as the Ad Studio. Closing here discards
          the preview locally; credits are charged only when the user approves
          and creates the final video. */}
      <div className="flex items-center justify-between gap-3 border-b border-slate-200/70 bg-slate-50 px-6 py-3 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-md bg-white ring-1 ring-slate-200 overflow-hidden flex-shrink-0">
            {draft.product_image_url ? (
              <img
                src={draft.product_image_url}
                alt={productName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-muted/30" />
            )}
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-foreground leading-tight truncate max-w-[280px]">
              {productName}
            </h2>
          </div>
        </div>
      </div>

      {/* Body — 60/40 split, fondo con gradient sutil del onboarding.
          Phase 5.4: el footer del modal ya no existe — las acciones
          principales (Cancelar + Crear mi video) viven al fondo de la
          columna izquierda para no robarle alto a la imagen. */}
      <div className="flex flex-1 min-h-0 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {/* LEFT — image + "Otra imagen". 45% del ancho para que la
            imagen y el panel de edición tengan más aire. */}
        <div className="w-[45%] min-w-0 border-r border-border/50 p-3 flex flex-col items-center justify-center gap-3 overflow-hidden">
          <div className="w-full max-w-md px-10">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Referencias visuales
            </p>
          </div>
          <AnimatePresence initial={false}>
            {activePreviewAsset ? (
              <motion.div
                key={`${activePreviewImageSrc}-${safeActivePreviewIndex}`}
                initial={false}
                animate={{ opacity: 1 }}
                exit={{ opacity: 1 }}
                transition={{ duration: 0 }}
                className="w-full max-w-md"
              >
                <div className="relative mx-auto flex w-fit items-center justify-center px-8">
                  {activePreviewAssets.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => goPreviewImage(-1)}
                        className="absolute left-1 top-1/2 z-20 h-9 w-9 -translate-y-1/2 rounded-lg bg-white/75 text-slate-500 ring-1 ring-slate-200/80 backdrop-blur-sm transition hover:bg-white hover:text-primary hover:ring-primary/25"
                        aria-label={t("video-preview-asset-prev", "Imagen anterior")}
                      >
                        <ChevronLeft className="mx-auto h-4 w-4" strokeWidth={1.8} />
                      </button>
                      <button
                        type="button"
                        onClick={() => goPreviewImage(1)}
                        className="absolute right-1 top-1/2 z-20 h-9 w-9 -translate-y-1/2 rounded-lg bg-white/75 text-slate-500 ring-1 ring-slate-200/80 backdrop-blur-sm transition hover:bg-white hover:text-primary hover:ring-primary/25"
                        aria-label={t("video-preview-asset-next", "Imagen siguiente")}
                      >
                        <ChevronRight className="mx-auto h-4 w-4" strokeWidth={1.8} />
                      </button>
                    </>
                  )}

                  <div
                    className="relative h-[clamp(440px,70vh,590px)] max-w-full aspect-[9/16] rounded-lg overflow-hidden bg-gradient-to-br from-slate-50 via-white to-indigo-50"
                    style={{
                      boxShadow:
                        "0 14px 34px -18px hsl(226 76% 58% / 0.34), 0 0 0 1px hsl(226 76% 58% / 0.12)",
                    }}
                  >
                    {!activePreviewImageSrc ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-indigo-50 via-white to-slate-50 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin text-primary/60" />
                        <p className="max-w-[190px] text-center text-xs leading-snug">
                          {t("video-preview-asset-preparing", "Preparando esta imagen...")}
                        </p>
                      </div>
                    ) : activePreviewImageFailed ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-muted/20 text-muted-foreground">
                        <ImageIcon className="h-10 w-10" />
                        <p className="max-w-[180px] text-center text-xs leading-snug">
                          {t("video-preview-asset-load-error", "No pudimos cargar esta imagen")}
                        </p>
                      </div>
                    ) : (
                      <>
                        {!activePreviewImageLoaded && (
                          <div className="absolute inset-0 z-0 overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-slate-50">
                            <div className="absolute inset-y-0 -left-1/2 w-1/2 skew-x-[-18deg] bg-white/70 blur-xl animate-shine" />
                            <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2 text-muted-foreground">
                              <Loader2 className="h-5 w-5 animate-spin text-primary/60" />
                              <p className="text-[11px] font-medium">
                                {t("video-preview-image-loading", "Cargando imagen...")}
                              </p>
                            </div>
                          </div>
                        )}
                        <img
                          src={activePreviewImageSrc}
                          alt={activePreviewAsset.label}
                          onLoad={() => setLoadedPreviewImageSrc(activePreviewImageSrc)}
                          onError={() => {
                            if (
                              draft.base_image_url &&
                              activePreviewAsset.url === draft.base_image_url &&
                              failedBaseImageUrl !== draft.base_image_url
                            ) {
                              setFailedBaseImageUrl(draft.base_image_url);
                            }
                            const failedUrl = activePreviewAsset.url;
                            if (!failedUrl) return;
                            setFailedPreviewImageUrls((prev) =>
                              prev.includes(failedUrl) ? prev : [...prev, failedUrl]
                            );
                          }}
                          className={cn(
                            "relative z-10 h-full w-full object-contain",
                            activePreviewAsset.source === "product" && "bg-white",
                            activePreviewImageLoaded ? "opacity-100" : "opacity-0"
                          )}
                        />
                      </>
                    )}

                    <span className="absolute right-3 top-3 z-20 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold tabular-nums text-foreground shadow-sm ring-1 ring-black/5">
                      {safeActivePreviewIndex + 1}/{previewImageAssets.length}
                    </span>

                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent px-4 pb-4 pt-10 text-white">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{activePreviewAsset.label}</p>
                        <p className="truncate text-[11px] text-white/75">{activePreviewAsset.subtitle}</p>
                      </div>
                    </div>

                    {activePreviewAsset?.target && (
                      <div className="absolute inset-x-0 bottom-4 z-20 flex flex-col items-center gap-2 px-4">
                        {!imageInstructionOpen && (
                          <button
                            type="button"
                            onClick={() => setImageInstructionOpen(true)}
                            disabled={!canRegen || isRegenImage}
                            title={
                              regenLeft > 0
                                ? t("video-preview-regen-left", `${regenLeft} regeneraciones disponibles`)
                                : t("video-preview-regen-none", "Sin regeneraciones disponibles")
                            }
                            className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-[11px] font-semibold text-slate-800 shadow-sm ring-1 ring-black/10 backdrop-blur-sm transition hover:bg-white hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isRegenImage ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3.5 w-3.5" />
                            )}
                            {isRegenImage
                              ? t("video-preview-regen-image-loading", "Regenerando...")
                              : t("video-preview-regen-active-image", "Regenerar")}
                          </button>
                        )}

                        <AnimatePresence>
                          {imageInstructionOpen && (
                            <motion.div
                              key="image-regeneration-instruction"
                              initial={{ opacity: 0, y: 8, scale: 0.98 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              exit={{ opacity: 0, y: 6, scale: 0.98 }}
                              transition={{ duration: 0.18 }}
                              className="w-[280px] max-w-full rounded-2xl bg-white/95 p-3 text-left shadow-xl ring-1 ring-black/10 backdrop-blur-md"
                            >
                              <p className="text-[11px] font-semibold text-slate-900">
                                {t("video-preview-regen-instruction-title", "¿Qué quieres cambiar?")}
                              </p>
                              <textarea
                                value={imageInstruction}
                                onChange={(event) => setImageInstruction(event.target.value)}
                                placeholder={t(
                                  "video-preview-regen-instruction-placeholder",
                                  "Ej. que mire a cámara, fondo más casero, ropa más casual..."
                                )}
                                className="mt-2 h-20 w-full resize-none rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-[11px] leading-snug text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-primary/40 focus:ring-2 focus:ring-primary/10"
                              />
                              <div className="mt-2 flex items-center justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setImageInstructionOpen(false);
                                    setImageInstruction("");
                                  }}
                                  className="rounded-lg px-2.5 py-1.5 text-[11px] font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                                >
                                  {t("video-preview-regen-cancel", "Cancelar")}
                                </button>
                                <button
                                  type="button"
                                  onClick={handleRegenerateActiveImage}
                                  disabled={!canRegen || isRegenImage || imageInstruction.trim().length < 3}
                                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {isRegenImage ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Sparkles className="h-3 w-3" />
                                  )}
                                  {t("video-preview-regen-apply", "Regenerar")}
                                </button>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}

                    <AnimatePresence>
                      {isActivePreviewImageRegenerating && (
                        <motion.div
                          key="preview-image-regenerating"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.18 }}
                          className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-black/50 backdrop-blur-sm"
                        >
                          <div className="relative">
                            <Loader2 className="h-12 w-12 animate-spin text-white" />
                            <Sparkles className="absolute inset-0 m-auto h-5 w-5 animate-pulse-glow text-white" />
                          </div>
                          <p className="text-xs font-medium text-white/90">
                            {t("video-preview-image-generating-overlay", "Generando nueva versión...")}
                          </p>
                        </motion.div>
                      )}

                      {isActivePreviewImageRecentlyUpdated && !isActivePreviewImageRegenerating && (
                        <motion.div
                          key="preview-image-updated"
                          initial={{ opacity: 0, y: 8, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -6, scale: 0.98 }}
                          transition={{ duration: 0.22 }}
                          className="absolute left-1/2 top-4 z-30 -translate-x-1/2 rounded-full bg-white/95 px-3 py-1.5 text-[11px] font-semibold text-primary shadow-sm ring-1 ring-primary/15"
                        >
                          {t("video-preview-image-updated", "Imagen actualizada")}
                        </motion.div>
                      )}
                    </AnimatePresence>

                  </div>
                </div>

                {activePreviewAssets.length > 1 && (
                  <div className="mx-auto mt-2 flex max-w-[280px] items-center justify-center overflow-x-auto px-1 pb-1">
                    <div className="inline-flex items-center gap-1.5 rounded-lg bg-white/85 p-1.5 shadow-sm ring-1 ring-slate-200/80 backdrop-blur-sm">
                      {activePreviewAssets.map((asset, index) => {
                        const src = withPreviewRefreshToken(asset.url, previewImageRefreshTokens[index]);
                        const active = index === safeActivePreviewIndex;
                        return (
                          <button
                            key={`${asset.label}-${index}`}
                            type="button"
                            onClick={() => setActivePreviewImageIndex(index)}
                            className={cn(
                              "relative h-12 w-[34px] flex-shrink-0 overflow-hidden rounded-md bg-white transition duration-75",
                              active
                                ? "shadow-sm ring-2 ring-primary"
                                : "opacity-80 ring-1 ring-slate-200 hover:opacity-100 hover:ring-primary/35"
                            )}
                            aria-label={`${asset.label}: ${asset.subtitle}`}
                          >
                            {src ? (
                              <img
                                src={src}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center bg-slate-50">
                                <ImageIcon className="h-3.5 w-3.5 text-slate-300" />
                              </div>
                            )}
                            {active && (
                              <span className="absolute inset-x-2 bottom-1 h-0.5 rounded-full bg-primary" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </motion.div>
            ) : (
              <div className="h-[clamp(440px,70vh,590px)] max-w-full aspect-[9/16] rounded-lg border border-border bg-muted/20 flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-primary animate-spin" />
              </div>
            )}
          </AnimatePresence>

        </div>

        {/* RIGHT — editable content (55% del modal) */}
        <div className="w-[55%] min-w-0 flex flex-col overflow-hidden">
          {/* Sin save bar superior — los dos editores (personalidad y
              guión) tienen sus propias acciones DENTRO del card, abajo,
              pegadas al input que el usuario está modificando. */}
          <div className="flex-1 min-h-0 overflow-y-auto px-8 py-6 space-y-6">
            {/* Phase 2 V2 — "Personalidad" section removed. The pattern
                (morning_ritual / testimonial_discovery / etc) is an internal
                director decision surfaced here was confusing for users — it
                implied a high-value knob when really it was just showing the
                roll result. Regenerate-for-a-different-vibe still works by
                cancelling the draft and creating a new one. Script + image
                regens stay (those ARE user-facing decisions). */}

            {/* Guión del video — hidden for silent modeling (no dialogue).
                Voice-over modeling DOES show the 3-beat TTS script so the
                user can review it before approving. */}
            {!isModelingSilent && <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                {t("video-preview-script-section", "Guión del video")}
              </p>

              {editingScript ? (
                <div className="rounded-xl border border-primary/40 bg-gradient-to-br from-primary/5 to-accent/5 p-4 space-y-3">
                  <textarea
                    value={fullScriptDraft}
                    onChange={(e) => setFullScriptDraft(e.target.value)}
                    rows={6}
                    autoFocus
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] leading-5 focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none"
                    placeholder={t("video-preview-script-placeholder", "Escribe el guión completo del video...")}
                  />
                  <div className="flex items-center justify-between gap-3">
                    {isModelingVoiceover && draftDuration && (
                      <span className="text-[11px] text-muted-foreground leading-snug flex-1">
                        {t(
                          "video-preview-script-duration-hint",
                          `Límite calibrado para que el audio entre en ${draftDuration}s sin cortarse.`,
                        ).replace("{duration}", String(draftDuration))}
                      </span>
                    )}
                    <span
                      className={`text-xs tabular-nums ${
                        scriptOverLimit
                          ? "text-red-500 font-semibold"
                          : scriptNearLimit
                            ? "text-amber-600 font-medium"
                            : "text-muted-foreground"
                      }`}
                    >
                      {totalWords}/{wordLimit} {t("video-preview-words", "palabras")}
                    </span>
                  </div>
                  {/* Acciones del editor del guión — dentro del card,
                      abajo, pegadas al textarea. */}
                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={isBusy}
                      className="px-3 py-1.5 rounded-full text-xs font-medium text-muted-foreground hover:bg-white/60 transition-colors disabled:opacity-50"
                    >
                      {t("video-preview-edit-cancel", "Cancelar")}
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveScript}
                      disabled={!canSaveScript}
                      className="px-4 py-2 rounded-full text-xs font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-1.5"
                    >
                      {isEditingScriptInFlight ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {t("video-preview-edit-saving", "Guardando...")}
                        </>
                      ) : (
                        <>
                          <Pencil className="h-3.5 w-3.5" />
                          {t("video-preview-save-changes", "Guardar cambios")}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-border bg-background p-3 hover:border-primary/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <p className="flex-1 text-[13px] text-foreground leading-5 whitespace-pre-wrap">
                      {fullScript}
                    </p>
                    <button
                      type="button"
                      onClick={() => setEditingScript(true)}
                      disabled={isBusy}
                      aria-label={t("video-preview-edit-script-aria", "Editar guión")}
                      className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>}

            {/* Silent modeling — no voice picker, just the fallback copy. */}
            {isModelingSilent && (
              <div className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 p-4 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3 text-primary" />
                  {t("video-preview-silent-label", "Video silencioso")}
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t(
                    "video-preview-silent-copy",
                    "La persona muestra el producto con expresiones naturales — sin voz."
                  )}
                </p>
              </div>
            )}

            {/* Phase 2 V3 + Phase 6 V4b (Apr 20 2026) — voice section.
                Driven by audio_mode:
                · professional → VoiceLibrary (curated Gemini voices + sliders)
                · own          → OwnVoicePanel (display uploaded file + re-upload)
                · fast         → FastVoiceHint (modeling-only; UGC doesn't have it)
                · undefined / legacy drafts → treat as professional
                Renders for BOTH:
                · isModelingVoiceover → B-roll + voz en off (same as before)
                · isUgc               → UGC testimonial (new, Apr 20)
                  Backend UGC pipeline uses Gemini TTS + sync-lipsync v2 PRO,
                  so the selected voice_id feeds the post-Kling lipsync step.
                Non-voiceover modeling (silent) renders nothing, same as before. */}
            {(isModelingVoiceover || isUgc) && (() => {
              const mode: string = (draft.audio_mode as string) ?? "professional";
              if (mode === "own") {
                return <OwnVoicePanel draft={draft} t={t} />;
              }
              // "fast" (Kling inline audio) only applies to modeling voice-over.
              // UGC always uses Gemini TTS + lipsync — no fast path.
              if (mode === "fast" && isModelingVoiceover) {
                return (
                  <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.04] to-accent/[0.03] p-4 space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      {t("video-preview-fast-audio-label", "Voz rápida (generada con el video)")}
                    </p>
                    <p className="text-[12px] text-foreground/80 leading-relaxed">
                      {t(
                        "video-preview-fast-audio-desc",
                        "No hay voces que elegir: el video y la voz se generan juntos en una sola pasada. Más rápido, pero la voz puede variar entre videos.",
                      )}
                    </p>
                  </div>
                );
              }
              // professional (default)
              // Phase 2 V4 (Apr 18 2026) — stale-audio pill.
              // When the user edits the script, the backend sets
              // payload.user_edited=true. If there is already an audio_url
              // (TTS was generated before the edit), that audio is now
              // out-of-sync with the displayed script. Show a dismissible
              // notice pointing to the voice picker so the user knows they
              // need to re-generate. Cleared server-side when TTS runs again.
              const scriptWasEdited =
                (draft.director_payload as Record<string, unknown> | undefined)?.user_edited === true;
              const audioMayBeStale = scriptWasEdited && !!draft.audio_url;
              return (
                <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.04] to-accent/[0.03] p-4 space-y-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Mic className="h-3.5 w-3.5 text-primary" />
                    {t("video-preview-voice-pick-label", "Elige la voz")}
                  </p>

                  {audioMayBeStale && (
                    <div className="rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 flex items-start gap-2">
                      <RefreshCw className="h-3.5 w-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
                      <p className="text-[11px] leading-relaxed text-amber-900">
                        {t(
                          "video-preview-audio-stale-pill",
                          "Editaste el guion. Vuelve a elegir una voz para que el audio coincida con el nuevo texto.",
                        )}
                      </p>
                    </div>
                  )}

                  <VoiceLibrary
                    selectedVoiceId={selectedVoice?.voice_id ?? null}
                    preferredGender={preferredVoiceGender}
                    accentCity={accentCity}
                    onAccentCityChange={setAccentCity}
                    musicPrompt={musicPrompt}
                    onMusicPromptChange={(value) => {
                      musicPromptTouchedRef.current = true;
                      setMusicPrompt(value);
                    }}
                    onSelectVoice={(v) => {
                      setSelectedVoice(v);
                      if (v.default_settings) {
                        setVoiceSettings({
                          speed: v.default_settings.speed,
                          stability: v.default_settings.stability,
                          similarity: v.default_settings.similarity_boost,
                          style: v.default_settings.style,
                        });
                      }
                    }}
                    settings={voiceSettings}
                    onSettingsChange={setVoiceSettings}
                    disabled={storeStatus === "generating_audio" || isBusy}
                  />

                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    {t(
                      "video-preview-voice-hint-simple",
                      "Toca play en cada voz para escucharla. Los ajustes se aplican al crear el video final.",
                    )}
                  </p>
                </div>
              );
            })()}

            <details className="group rounded-2xl border border-slate-200/80 bg-white/70 shadow-sm open:bg-white">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:text-primary [&::-webkit-details-marker]:hidden">
                <span>{t("video-preview-advanced-kling-title", "Opciones avanzadas")}</span>
                <ChevronRight className="h-4 w-4 text-slate-400 transition group-open:rotate-90 group-open:text-primary" />
              </summary>
              <div className="border-t border-slate-100 px-4 pb-4 pt-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-700">
                      {t("video-preview-kling-json-label", "JSON del video")}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {t("video-preview-kling-json-desc", "Payload real calculado por backend antes de aprobar.")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!klingPreviewJson) return;
                      try {
                        await copyTextToClipboard(klingPreviewJson);
                        setCopiedAdvancedPayload(true);
                        window.setTimeout(() => setCopiedAdvancedPayload(false), 1400);
                      } catch {
                        toast.error(t("video-preview-copy-error", "No pudimos copiar el JSON."));
                      }
                    }}
                    disabled={!klingPreviewJson}
                    className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 shadow-sm transition hover:border-primary/30 hover:text-primary disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    {copiedAdvancedPayload ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                    {copiedAdvancedPayload
                      ? t("video-preview-copied", "Copiado")
                      : t("copy", "Copiar")}
                  </button>
                </div>
                {renderPayloadLoading ? (
                  <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-4 text-xs text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    {t("video-preview-kling-json-loading", "Cargando payload real...")}
                  </div>
                ) : renderPayloadError ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-xs leading-relaxed text-amber-900">
                    {renderPayloadError}
                  </div>
                ) : (
                  <pre className="max-h-72 overflow-auto rounded-xl border border-slate-200 bg-slate-950 p-3 text-[11px] leading-relaxed text-slate-100 shadow-inner">
                    <code>{klingPreviewJson}</code>
                  </pre>
                )}
              </div>
            </details>

            {/* Phase 2 V2 — "Otra versión" intentionally removed per product
                decision: el flujo es lineal. El user elige voz + opcionalmente
                edita el guión o cambia personalidad, y le da Continuar. No
                le regalamos una regen blind de todo el draft. */}
          </div>

          {/* Footer del panel derecho — barra inferior solo del 42% de
              la columna derecha. Costo arriba (sutil, sin pill), Cancelar
              + Crear mi video alineados a la derecha abajo. */}
          <div className="flex flex-col items-center gap-2 px-6 py-4 border-t border-border flex-shrink-0 bg-background">
            <p className="text-[11px] text-muted-foreground tabular-nums">
              {t("video-cost", "costo")}{" "}
              <span className="font-semibold text-fluxi">
                {draft.credits_charged ?? 0} {t("video-credits", "créditos")}
              </span>
            </p>
            {/* Explicit progress hint for the pre-approve save + approve window. */}
            {isModelingVoiceover && !modelingVoiceoverAssetsReady && !isApproving && !isCreatingVideo && (
              <p className="text-[11px] text-muted-foreground leading-relaxed text-center max-w-[300px]">
                {t(
                  "video-preview-progress-assets",
                  "Preparando imágenes del video. Ya puedes revisar el guión y la voz."
                )}
              </p>
            )}
            {(isApproving || isCreatingVideo) && (
              <p className="text-[11px] text-muted-foreground leading-relaxed text-center max-w-[280px]">
                {storeStatus === "generating_audio"
                  ? t("video-preview-progress-audio", "Preparando la voz…")
                  : t("video-preview-progress-dispatch", "Enviando el video a render. Se cerrará cuando quede en cola.")}
              </p>
            )}
            {createVideoError && !isCreatingVideo && !isApproving && (
              <p className="max-w-[360px] rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-center text-[11px] leading-relaxed text-red-700">
                {createVideoError}
              </p>
            )}
            <div className="flex w-full items-center justify-between gap-3">
              <button
                type="button"
                disabled={isBusy || editingScript || isCreatingVideo}
                onClick={() => {
                  reset();
                  onBack();
                }}
                className={cn(
                  "flex items-center gap-2 px-6 py-3 text-sm text-gray-600 transition-colors hover:text-gray-900",
                  (isBusy || editingScript || isCreatingVideo) && "cursor-not-allowed opacity-40"
                )}
              >
                <ArrowLeft className="h-4 w-4" />
                Volver
              </button>
              <ButtonFluxi
                icon={
                  (isApproving || isCreatingVideo) ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )
                }
                text={
                  (isApproving || isCreatingVideo)
                    ? t("video-preview-approving", "Creando...")
                    : isModelingVoiceover && !modelingVoiceoverAssetsReady
                      ? t("video-preview-assets-loading-button", "Preparando imágenes...")
                    : t("video-preview-approve", "Crear mi video")
                }
                onClick={async () => {
                  // Layer 3 guard (Phase 2 V3 fix) — synchronous debounce
                  // to prevent triple-click creating 3 parallel requests.
                  // useState's setter is asynchronous; by the time React
                  // re-renders with isApproving=true and disables the
                  // button, two more clicks may have already fired. A ref
                  // flips in the current tick and blocks every
                  // subsequent click on THIS instance of the handler.
                  if (approveInFlightRef.current) return;
                  approveInFlightRef.current = true;
                  // Immediate visual feedback for the short pre-approve save
                  // before approve() kicks in.
                  setIsCreatingVideo(true);
                  setCreateVideoError(null);
                  try {
                    if (!draft) return;
                    const draftSnapshot = draft;
                    const submitToGallery = (
                      queuedDraft: VideoDraftResponse,
                      displayStatus: "preparing" | "in-flight" | "error" = "in-flight"
                    ) => {
                      if (onSubmitted && !submittedToGalleryRef.current) {
                        submittedToGalleryRef.current = true;
                        trackInFlightDraft(queuedDraft, displayStatus);
                        onSubmitted();
                        return;
                      }
                      trackInFlightDraft(queuedDraft, displayStatus);
                    };
                    const markCreateError = (message: string) => {
                      toast.error(message);
                      setCreateVideoError(message);
                    };
                    const recoverSubmittedDraft = async () => {
                      const { res: latestDraft } = await getVideoDraft(draftSnapshot.reference_id);
                      if (!isPostApproveDraft(latestDraft)) return false;
                      submitToGallery(
                        {
                          ...latestDraft,
                          status: latestDraft.status || VIDEO_DRAFT_STATUS.GENERATING_IMAGE,
                        },
                        "in-flight"
                      );
                      return true;
                    };

                    // CE voiceover only saves the selected voice here; the
                    // actual synthesis belongs to Conversation Engine render.
                    // UGC testimonial keeps the older preview-side audio path.
                    const mode: string = (draftSnapshot.audio_mode as string) ?? "professional";
                    if (isModelingVoiceover && mode === "professional" && selectedVoice) {
                      const scriptTrimmed = fullScriptDraft.trim();
                      const { error: voiceConfigError } = await setVideoDraftVoiceConfig(draftSnapshot.reference_id, {
                        voice_id: selectedVoice.voice_id,
                        voice_name: selectedVoice.display_name,
                        provider: selectedVoice.provider ?? "gemini",
                        speed: voiceSettings.speed,
                        stability: voiceSettings.stability,
                        similarity_boost: voiceSettings.similarity,
                        style: voiceSettings.style,
                        language_code: "es",
                        accent_city: accentCity.trim() || "Medellín",
                        music_prompt: musicPrompt.trim() || undefined,
                        script_override: scriptDirty && scriptTrimmed.length > 0 ? scriptTrimmed : undefined,
                      });
                      if (voiceConfigError) {
                        if (
                          isDraftNotEditableConflict(voiceConfigError) &&
                          await recoverSubmittedDraft()
                        ) {
                          return;
                        }
                        markCreateError(
                          getErrorMessage(voiceConfigError) ||
                            t("video-preview-voice-config-error", "No pudimos guardar la voz elegida.")
                        );
                        return;
                      }
                    } else if (isUgc && mode === "professional" && selectedVoice) {
                      const scriptTrimmed = fullScriptDraft.trim();
                      if (scriptTrimmed.length > 0) {
                        const { error: audioError } = await generateVideoDraftAudio(draftSnapshot.reference_id, {
                          voice_id: selectedVoice.voice_id,
                          voice_name: selectedVoice.display_name,
                          provider: selectedVoice.provider ?? "gemini",
                          speed: voiceSettings.speed,
                          stability: voiceSettings.stability,
                          similarity_boost: voiceSettings.similarity,
                          style: voiceSettings.style,
                          language_code: "es",
                          accent_city: accentCity.trim() || "Medellín",
                          // UGC backend reads the scripts from
                          // director_payload directly (script_part_a /
                          // script_part_b), so the override is a no-op there.
                          // Modeling still uses it to pass the edited
                          // beat-based script text.
                          script_override: scriptTrimmed,
                        });
                        if (audioError) {
                          markCreateError(
                            getErrorMessage(audioError) ||
                              t("video-preview-audio-error", "No pudimos generar la voz en off.")
                          );
                          return;
                        }
                      }
                    }

                    const { res: approvedDraft, error: approveError } =
                      await approveVideoDraft(draftSnapshot.reference_id);
                    if (approveError || !approvedDraft) {
                      if (await recoverSubmittedDraft()) {
                        return;
                      }
                      markCreateError(
                        (approveError ? getErrorMessage(approveError) : null) ||
                          t("video-preview-approve-error", "No pudimos enviar el video a render.")
                      );
                      return;
                    }

                    submitToGallery(
                      {
                        ...approvedDraft,
                        status: approvedDraft.status || VIDEO_DRAFT_STATUS.GENERATING_IMAGE,
                      },
                      "in-flight"
                    );
                  } finally {
                    // Clear the ref only after the full flow settles; if
                    // the user dismisses the modal via onDone() the ref
                    // resets naturally when the component unmounts.
                    approveInFlightRef.current = false;
                    if (!submittedToGalleryRef.current) {
                      setIsCreatingVideo(false);
                    }
                  }
                }}
                disabled={(() => {
                  // Local feedback: cover the full Crear-mi-video window.
                  if (isCreatingVideo) return true;
                  if (isBusy || editingScript) return true;
                  if (isModelingVoiceover && !modelingVoiceoverAssetsReady) return true;
                  if (!isModelingVoiceover) return false;
                  // Gate by audio_mode:
                  //   professional → must have picked a voice
                  //   own          → already gated at sidebar (audio already uploaded)
                  //   fast         → nothing extra required
                  const mode: string = (draft.audio_mode as string) ?? "professional";
                  if (mode === "professional") return !selectedVoice;
                  return false;
                })()}
                isEffect={!isBusy && !editingScript}
              />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────────
// Sub-component: OwnVoicePanel
// ─────────────────────────────────────────────────
// Phase 2 V3 — rendered in PreviewApproval when the draft's audio_mode
// is "own". Shows the file the user uploaded in the sidebar (via
// voice_config.uploaded_audio_url) with an inline <audio> player so they
// can listen before approving. No voice picker, no sliders — those are
// for the "professional" mode. The sidebar is the upload entry point, not
// this panel, so there's no re-upload affordance here; to change the
// audio the user closes the preview and goes back to the studio.

function OwnVoicePanel({
  draft,
  t,
}: {
  draft: VideoDraftResponse;
  t: (key: string, fallback: string) => string;
}) {
  const uploaded = draft.voice_config?.uploaded_audio_url || draft.audio_url || null;
  return (
    <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.04] to-accent/[0.03] p-4 space-y-3">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
        <Mic className="h-3.5 w-3.5 text-primary" />
        {t("video-preview-own-voice-label", "Tu voz")}
      </p>
      {uploaded ? (
        <div className="flex flex-col gap-2">
          <audio controls src={uploaded} className="w-full" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            {t(
              "video-preview-own-voice-ready",
              "Vamos a sincronizar tu audio con el video automáticamente cuando apruebes.",
            )}
          </p>
        </div>
      ) : (
        <p className="text-[12px] text-muted-foreground leading-relaxed">
          {t(
            "video-preview-own-voice-missing",
            "No encontramos tu audio. Vuelve al estudio y súbelo antes de crear el video.",
          )}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────
// Sub-component: DirectorLoader
// ─────────────────────────────────────────────────
// Loader rico para cuando el director Gemini está corriendo. Mensajes
// rotativos cada 3s con el mismo lenguaje visual del flujo de avatares.

const LOADING_MESSAGES = [
  {
    key: "video-preview-loading-msg-1",
    fallback: "Investigando anuncios similares exitosos",
  },
  {
    key: "video-preview-loading-msg-2",
    fallback: "Escribiendo un guión persuasivo",
  },
  {
    key: "video-preview-loading-msg-3",
    fallback: "Creando escenas hiper-realistas",
  },
  {
    key: "video-preview-loading-msg-4",
    fallback: "Definiendo el ritmo de la voz en off",
  },
  {
    key: "video-preview-loading-msg-5",
    fallback: "Planeando movimientos naturales de cámara",
  },
  {
    key: "video-preview-loading-msg-6",
    fallback: "Preparando referencias visuales consistentes",
  },
  {
    key: "video-preview-loading-msg-7",
    fallback: "Ajustando el tono para tu audiencia",
  },
  {
    key: "video-preview-loading-msg-8",
    fallback: "Organizando los recursos del video",
  },
];

function DirectorLoader({ title, subtitle }: { title: string; subtitle?: string }) {
  const displayTitle = title.replace(" los detalles de tu video", " los\ndetalles de tu video");

  return (
    <VideoPlanningLoader
      title={displayTitle}
      subtitle={subtitle}
      messages={LOADING_MESSAGES}
    />
  );
}

// Phase 6 V4b (Apr 20 2026) — removed unused ImageInstructionPanel
// sub-component. It was the inline panel that expanded when the user
// clicked "con instrucción" to regenerate the image with custom text.
// Removed together with its trigger per user feedback that the image
// regenerate controls were creating inconsistent previews (new image
// with old script that didn't match).
